import { Component, ElementRef, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { DwLoggingService, DwSecurityUserService } from '@devwareapps/devware-cap';

import { DwPubSubService } from '../../../../../../z-devware/pubsub/dw-pub-sub-service';
import { VidstackIframePlayerComponent } from '../../../../vidstack-iframe-player/vidstack-iframe-player.component';
import { VideoComponentBase } from '../../../../../models/video-component.base.interface';
import { VideoEventType } from '../../../../../models/video-event-type.enum';
import { VidstackIframePlayerConfig, VidstackIframePlayerEvent } from '../../../../../models/vidstack-iframe-player.model';
import { VideoMessagingService } from '../../../../../services/video-messaging.service';
import { VideoPlayerBaseComponent } from '../../../base/video-player-base.component';

@Component({
  selector: 'app-video-player-bunny-hls',
  template: `
    <div class="video-player-shell">
      <div [ngStyle]="videoPlayerStyle" class="video-player-container">
        <app-vidstack-iframe-player
          *ngIf="iframePlayerConfig"
          #iframePlayer
          class="video-player-surface bunny-hls-player-surface"
          [config]="iframePlayerConfig"
          (playerEvent)="onIframePlayerEvent($event)">
        </app-vidstack-iframe-player>
        <div class="video-playback-adjustment-badge" *ngIf="seekPreventionNoticeVisible" role="status" aria-live="polite">
          <i class="fa fa-exclamation-circle" [title]="seekPreventionNoticeTooltip" aria-hidden="true"></i>
          <span>{{seekPreventionNoticeText}}</span>
        </div>
      </div>
      <div *ngIf="diagnosticMode" class="bunny-hls-diagnostics" aria-live="polite">
        <span><strong>Position</strong> {{diagnosticPosition}}</span>
        <span><strong>Skipping</strong> {{diagnosticSkipMode}}</span>
        <span><strong>Autoplay</strong> {{diagnosticAutoplayMode}}</span>
        <span class="bunny-hls-diagnostics__state">
          {{diagnosticStatusText}}
          <i class="bunny-hls-diagnostics__orb" [class.is-green]="diagnosticState === 'green'"
            [class.is-orange]="diagnosticState === 'orange'" [class.is-red]="diagnosticState === 'red'"
            aria-hidden="true"></i>
        </span>
      </div>
    </div>
  `,
  styles: [`
    .bunny-hls-player-surface {
      display: block;
      height: 100%;
      width: 100%;
    }

    .bunny-hls-diagnostics {
      align-items: center;
      background: var(--surface-sunken, #f7f8fa);
      border: 1px solid var(--border-subtle, #dfe3e8);
      border-radius: var(--radius-lg, 8px);
      box-sizing: border-box;
      color: var(--text-secondary);
      display: flex;
      flex-wrap: wrap;
      font-size: var(--font-size-sm, 0.875rem);
      gap: var(--spacing-md, 16px);
      margin: var(--spacing-sm, 12px) auto 0;
      padding: var(--spacing-sm, 12px) var(--spacing-md, 16px);
      width: min(100% - 2 * var(--layout-gutter, 20px), var(--lv-column-max, var(--container-content, 1232px)));
    }

    .bunny-hls-diagnostics strong { color: var(--text-primary); margin-right: 4px; }
    .bunny-hls-diagnostics__state { align-items: center; display: inline-flex; gap: 8px; margin-left: auto; }
    .bunny-hls-diagnostics__orb {
      background: var(--warning-500, #f59e0b);
      border-radius: 50%;
      box-shadow: 0 0 0 3px color-mix(in srgb, var(--warning-500, #f59e0b) 18%, transparent);
      display: inline-block;
      height: 10px;
      width: 10px;
    }
    .bunny-hls-diagnostics__orb.is-green {
      background: var(--success-500, #22c55e);
      box-shadow: 0 0 0 3px color-mix(in srgb, var(--success-500, #22c55e) 18%, transparent);
    }
    .bunny-hls-diagnostics__orb.is-orange { background: var(--warning-500, #f59e0b); }
    .bunny-hls-diagnostics__orb.is-red {
      background: var(--danger-500, #ef4444);
      box-shadow: 0 0 0 3px color-mix(in srgb, var(--danger-500, #ef4444) 18%, transparent);
    }
  `]
})
export class VideoPlayerBunnyHlsComponent extends VideoPlayerBaseComponent implements VideoComponentBase, OnChanges, OnInit, OnDestroy {
  @ViewChild('iframePlayer') iframePlayer?: VidstackIframePlayerComponent;
  @Input() diagnosticMode = false;

  iframePlayerConfig?: VidstackIframePlayerConfig;
  videoPlayerStyle: any = { width: '100%', 'min-width': '350px' };
  seekPreventionNoticeVisible = false;
  seekPreventionNoticeText = 'Playback adjusted';
  seekPreventionNoticeTooltip = 'Seeking ahead is not allowed for this lesson.';
  diagnosticPosition = '0:00 / --:--';
  diagnosticSkipMode = 'Prevented';
  diagnosticAutoplayMode = 'Configured Off / Player --';
  diagnosticStatusText = 'Initializing';
  diagnosticState: 'green' | 'orange' | 'red' = 'orange';

  private initCompletedEmitted = false;
  private playbackStarted = false;
  private playbackCompleted = false;
  private lessonCompleted = false;
  private previousWidth: number;
  private previousViewportHeight: number;
  private resizeObserver: any;
  private seekPreventionNoticeTimeout: any;

  constructor(
    dwSecurityUserService: DwSecurityUserService,
    videoMessagingService: VideoMessagingService,
    dwPubSubService: DwPubSubService,
    private hostElementRef: ElementRef,
    private dwLoggingService: DwLoggingService
  ) {
    super(dwSecurityUserService, videoMessagingService, dwPubSubService);
  }

  ngOnInit(): void {
    super.ngOnInit();
    setTimeout(() => {
      this.setVideoPlayerStyle();
      this.setupResizeObserver();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    if (!changes.video && (changes.playback || changes.videoStreamingProvider
      || changes.allowSeekOverride || changes.preventSeekOverride)) {
      this.setupVideo();
    }
  }

  ngOnDestroy(): void {
    this.clearSeekPreventionNotice();
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    super.ngOnDestroy();
  }

  setupVideo(): void {
    super.setupVideo();
    const source = this.playback?.OnlineSource;
    if (!source?.Url || source.Format?.toLowerCase() !== 'hls') {
      this.iframePlayerConfig = undefined;
      return;
    }

    this.furthestWatchedTime = Math.max(this.furthestWatchedTime || 0, this.playback?.FurthestWatchedSeconds || 0);
    this.videoDuration = this.playback?.DurationSeconds || 0;
    this.lessonCompleted = this.playback?.LessonCompleted === true;
    this.currentTime = this.lessonCompleted ? 0 : this.playback?.ResumePositionSeconds || 0;
    this.userSeeked = false;
    this.initCompletedEmitted = false;
    this.playbackStarted = false;
    this.playbackCompleted = false;
    this.diagnosticPosition = this.formatPosition(this.currentTime, this.videoDuration);
    this.diagnosticSkipMode = this.allowSeek ? 'Allowed' : 'Prevented';
    this.diagnosticAutoplayMode = `Configured ${this.videoStreamingProvider?.AutoPlay === true ? 'On' : 'Off'} / Player --`;
    this.diagnosticStatusText = 'Initializing';
    this.diagnosticState = 'orange';
    this.clearSeekPreventionNotice();
    this.iframePlayerConfig = this.buildPlayerConfig(source.Url);
  }

  pauseVideo(): void {
    this.iframePlayer?.pause();
  }

  onIframePlayerEvent(event: VidstackIframePlayerEvent): void {
    switch (event.eventType) {
      case 'ready':
        const configuredAutoplay = this.videoStreamingProvider?.AutoPlay === true ? 'On' : 'Off';
        const playerAutoplay = event.data?.autoplay === true ? 'On' : 'Off';
        this.diagnosticAutoplayMode = `Configured ${configuredAutoplay} / Player ${playerAutoplay}`;
        if (!this.initCompletedEmitted) {
          this.initCompletedEmitted = true;
          this.diagnosticStatusText = 'Ready';
          this.diagnosticState = 'green';
          this.onVideoEvent(VideoEventType.onReady);
          this.initCompleted.emit(true);
        }
        break;
      case 'playing':
        this.playbackStarted = true;
        this.playbackCompleted = false;
        this.onVideoEvent(VideoEventType.videoStarted);
        this.notifyVideoStarted();
        break;
      case 'pause':
        if (this.playbackStarted && !this.playbackCompleted) {
          this.onVideoEvent(VideoEventType.videoPaused);
        }
        break;
      case 'position':
        this.handlePosition(event.data);
        break;
      case 'seeked':
        this.onVideoEvent(VideoEventType.videoSeek, event.data);
        break;
      case 'seek-prevented':
        this.handlePreventedSeek(event.data);
        break;
      case 'ended':
        this.playbackCompleted = true;
        this.currentTime = this.videoDuration || this.currentTime;
        this.furthestWatchedTime = Math.max(this.furthestWatchedTime || 0, this.videoDuration || 0);
        this.diagnosticPosition = this.formatPosition(this.currentTime, this.videoDuration);
        this.diagnosticStatusText = 'Completed';
        this.diagnosticState = 'green';
        this.onVideoEvent(VideoEventType.videoCompleted);
        break;
      case 'error':
        this.handlePlayerError(event.data);
        break;
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.setVideoPlayerStyle();
  }

  private buildPlayerConfig(sourceUrl: string): VidstackIframePlayerConfig {
    const source = new URL(sourceUrl);
    const requestQueryParameters: { [key: string]: string } = {};
    ['token', 'token_path', 'expires'].forEach(key => {
      const value = source.searchParams.get(key);
      if (value) {
        requestQueryParameters[key] = value;
      }
    });

    const posterUrl = this.createPosterUrl(source, requestQueryParameters);
    const captionUrl = this.createCaptionUrl(source);

    return {
      sourceUrl,
      posterUrl,
      thumbnailPreviewUrl: this.playback?.ThumbnailPreviewUrl,
      title: this.video?.VideoName || 'Lesson video',
      autoplay: this.videoStreamingProvider?.AutoPlay === true,
      allowSeek: this.allowSeek,
      playbackRates: [0.5, 0.75, 1, 1.25, 1.5],
      requestOrigin: source.origin,
      requestQueryParameters,
      textTracks: captionUrl
        ? [{
          src: captionUrl,
          kind: 'captions',
          label: 'English (Auto)',
          language: 'en',
          type: 'vtt',
          default: false
        }]
        : [],
      resumePosition: this.lessonCompleted ? 0 : this.playback?.ResumePositionSeconds || 0,
      furthestWatchedTime: this.furthestWatchedTime || 0,
      seekThresholdSeconds: this.playback?.SeekThresholdSeconds || this.videoStreamingProvider?.SeekThreshholdSeconds || 5
    };
  }

  private createCaptionUrl(source: URL): string | undefined {
    if (!/\/playlist\.m3u8$/i.test(source.pathname)) {
      return undefined;
    }

    const caption = new URL(source.toString());
    caption.pathname = caption.pathname.replace(/\/playlist\.m3u8$/i, '/captions/en-auto.vtt');
    caption.search = '';
    caption.hash = '';
    return caption.toString();
  }

  private createPosterUrl(source: URL, requestQueryParameters: { [key: string]: string }): string {
    let poster: URL;

    try {
      poster = this.video?.ThumbnailUrl
        ? new URL(this.video.ThumbnailUrl)
        : new URL(source.toString());
    } catch {
      poster = new URL(source.toString());
    }

    if (!this.video?.ThumbnailUrl) {
      poster.pathname = poster.pathname.replace(/\/playlist\.m3u8$/i, '/thumbnail_1.jpg');
    }

    ['token', 'token_path', 'expires'].forEach(key => poster.searchParams.delete(key));
    if (poster.origin === source.origin) {
      Object.keys(requestQueryParameters).forEach(key => {
        poster.searchParams.set(key, requestQueryParameters[key]);
      });
    }

    return poster.toString();
  }

  private handlePosition(data?: { [key: string]: any }): void {
    const duration = Number(data?.duration);
    if (Number.isFinite(duration) && duration > 0) {
      this.videoDuration = duration;
    }

    const currentTime = Number(data?.currentTime);
    if (!Number.isFinite(currentTime)) {
      return;
    }

    this.currentTime = currentTime;
    this.userSeeked = false;
    const playerFurthestWatchedTime = Number(data?.furthestWatchedTime);
    if (!this.lessonCompleted && Number.isFinite(playerFurthestWatchedTime)) {
      this.furthestWatchedTime = Math.max(this.furthestWatchedTime || 0, playerFurthestWatchedTime);
    }

    this.diagnosticPosition = this.formatPosition(this.currentTime || 0, this.videoDuration || 0);
    if (this.playbackStarted && !this.playbackCompleted) {
      this.onVideoEvent(VideoEventType.videoProgress);
    }
  }

  private handlePreventedSeek(data?: { [key: string]: any }): void {
    const requestedTime = Number(data?.requestedTime);
    const returnToPosition = Number(data?.returnToPosition);
    this.userSeeked = true;
    this.recordPreventedSeek(
      Number.isFinite(requestedTime) ? requestedTime : this.currentTime || 0,
      Number.isFinite(returnToPosition) ? returnToPosition : this.furthestWatchedTime || 0
    );
  }

  private recordPreventedSeek(requestedTime: number, returnToPosition: number): void {
    this.showSeekPreventionNotice();
    this.onVideoEvent(VideoEventType.videoSeek, { requestedTime, returnToPosition, prevented: true });
  }

  private handlePlayerError(data?: { [key: string]: any }): void {
    if (this.isPlaybackAbort(data)) {
      return;
    }

    this.initCompleted.emit(false);
    this.diagnosticStatusText = this.initCompletedEmitted ? 'Playback error' : 'Initialization failed';
    this.diagnosticState = this.initCompletedEmitted ? 'orange' : 'red';
    const message = data?.message || 'Bunny HLS playback failed.';
    const error = new Error(message);
    this.dwLoggingService.logError(message, error, null, true);
    this.dwPubSubService.emitDebug('Error', message, data);
  }

  private isPlaybackAbort(data?: { [key: string]: any }): boolean {
    const name = String(data?.name || data?.error?.name || '').toLowerCase();
    const message = String(data?.message || data?.error?.message || '').toLowerCase();
    return name === 'aborterror'
      || message === 'the operation was aborted.'
      || message.includes('the play() request was interrupted');
  }

  private showSeekPreventionNotice(): void {
    this.clearSeekPreventionNotice();
    this.seekPreventionNoticeVisible = true;
    this.seekPreventionNoticeTimeout = setTimeout(() => {
      this.seekPreventionNoticeVisible = false;
      this.seekPreventionNoticeTimeout = null;
    }, 9000);
  }

  private clearSeekPreventionNotice(): void {
    if (this.seekPreventionNoticeTimeout) {
      clearTimeout(this.seekPreventionNoticeTimeout);
      this.seekPreventionNoticeTimeout = null;
    }
    this.seekPreventionNoticeVisible = false;
  }

  private setupResizeObserver(): void {
    const resizeObserverType = (window as any).ResizeObserver;
    const host = this.hostElementRef?.nativeElement as HTMLElement;
    if (!resizeObserverType || !host || this.resizeObserver) {
      return;
    }
    this.resizeObserver = new resizeObserverType(() => this.setVideoPlayerStyle());
    this.resizeObserver.observe(host.firstElementChild || host);
  }

  private formatPosition(currentTime: number, duration: number): string {
    return `${this.formatTime(currentTime)} / ${duration > 0 ? this.formatTime(duration) : '--:--'}`;
  }

  private formatTime(totalSeconds: number): string {
    const safeSeconds = Math.max(0, Math.floor(totalSeconds || 0));
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const seconds = safeSeconds % 60;
    const minuteText = hours > 0 ? minutes.toString().padStart(2, '0') : minutes.toString();
    return `${hours > 0 ? `${hours}:` : ''}${minuteText}:${seconds.toString().padStart(2, '0')}`;
  }

  private setVideoPlayerStyle(): void {
    const host = this.hostElementRef?.nativeElement as HTMLElement;
    const width = host?.firstElementChild?.clientWidth || host?.clientWidth;
    if (!width || (width === this.previousWidth && window.innerHeight === this.previousViewportHeight)) {
      return;
    }
    this.previousWidth = width;
    this.previousViewportHeight = window.innerHeight;
    const offsetTop = Math.round(host.getBoundingClientRect()?.top || 0);
    const maxHeight = window.innerHeight - offsetTop - 100;
    const height = Math.round(width / (16 / 9)) + 2;
    let finalWidth = '100%';
    if (height > maxHeight && width > 850) {
      finalWidth = `${Math.round(maxHeight * (16 / 9)) - 2}px`;
    }
    this.videoPlayerStyle = { width: finalWidth, 'min-width': '350px' };
    this.videoWidthChanged.emit(finalWidth);
  }
}
